import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Droplets } from 'lucide-react';

const PerformanceInsights = ({ isDarkMode }) => {
  const providers = [
    { name: 'EverestCloud (Local)', latency: 8, display: '<10', color: 'emerald' },
    { name: 'AWS (Mumbai)', latency: 45, display: '45', color: 'slate' },
    { name: 'Azure (Central India)', latency: 52, display: '52', color: 'slate' },
    { name: 'GCP (Singapore)', latency: 65, display: '65', color: 'slate' },
  ];

  return (
    <div className="mt-16 space-y-16">
      {/* SECTION 1: LATENCY & GREEN ENERGY */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Latency Comparison Card */}
        <div className={`p-8 rounded-2xl border ${isDarkMode ? 'bg-slate-900/50 border-slate-800' : 'bg-white border-slate-200'} shadow-xl`}>
          <div className="flex items-center gap-3 mb-8">
            <Zap className="text-emerald-500" />
            <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>The Latency Gap (ms)</h3>
          </div>

          <div className="space-y-6">
            {providers.map((p) => (
              <div key={p.name} className="space-y-2">
                <div className="flex justify-between text-sm font-medium">
                  <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>{p.name}</span>
                  <span className={p.color === 'emerald' ? 'text-emerald-500 font-bold' : (isDarkMode ? 'text-slate-500' : 'text-slate-400')}>{p.display} ms</span>
                </div>
                <div className={`w-full h-3 rounded-full overflow-hidden ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${(p.latency / 80) * 100}%` }}
                    transition={{ duration: 1, delay: 0.2 }}
                    className={`h-full rounded-full ${p.color === 'emerald' ? 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]' : 'bg-slate-400'}`}
                  />
                </div>
              </div>
            ))}
          </div>
          <p className={`mt-6 text-xs italic ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            * Measured from ISP backbones in KATHMANDU. Azure data based on Central India region peering.
          </p>
        </div>

        {/* Green Energy - Hydro Powered Visualization */}
        <div className={`p-8 rounded-2xl border overflow-hidden relative ${isDarkMode ? 'bg-emerald-950/20 border-emerald-900/50' : 'bg-emerald-50 border-emerald-100'}`}>
          <div className="flex items-center gap-3 mb-4">
            <Droplets className="text-blue-500" />
            <h3 className={`text-xl font-bold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-900'}`}>100% Hydro-Powered</h3>
          </div>
          <p className={`text-sm mb-6 ${isDarkMode ? 'text-emerald-300/70' : 'text-emerald-700/80'}`}>
            Sustainable computing powered by the Himalayas. Our KATHMANDU, LALITPUR and BUTWAL zones run on pure renewable energy.
          </p>

          <div className="flex items-end gap-2 h-24">
            {[40, 70, 45, 90, 65, 80, 50, 85, 60, 95].map((h, i) => (
              <motion.div
                key={i}
                initial={{ height: 0 }}
                whileInView={{ height: `${h}%` }}
                transition={{ repeat: Infinity, repeatType: 'reverse', duration: 1.5 + i * 0.1 }}
                className="flex-1 bg-blue-500/40 rounded-t-sm"
              />
            ))}
          </div>
          <div className="absolute top-0 right-0 p-8 opacity-10">
            <Droplets size={120} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceInsights;
